import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, BookOpen, AlertTriangle, Copy } from "lucide-react";
import { useNav, VIEWS } from "@/lib/nav";
import { KeywordChip } from "@/components/KeywordChip";

export function sectionBadgeColor(section) {
  switch (section) {
    case "cardAbility":
      return "text-purple-300 border-purple-700/60";
    case "modelAbility":
      return "text-cyan-300 border-cyan-700/60";
    case "weaponAbility":
      return "text-amber-300 border-amber-700/60";
    case "feat":
      return "text-rose-300 border-rose-700/60";
    case "spell":
      return "text-emerald-300 border-emerald-700/60";
    default:
      return "text-muted-foreground";
  }
}

export function sectionLabel(section) {
  switch (section) {
    case "cardAbility":
      return "Card";
    case "modelAbility":
      return "Model";
    case "weaponAbility":
      return "Weapon";
    case "feat":
      return "Feat";
    case "spell":
      return "Spell";
    default:
      return section;
  }
}

const SPELL_STATS = ["COST", "RNG", "AOE", "POW", "DUR", "OFF"];

/** Spell stat line, laid out like the in-game card. */
export function SpellStats({ stats, testId = "spell-stats" }) {
  if (!stats) return null;
  return (
    <div className="inline-flex gap-3 rounded border border-emerald-800/50 bg-emerald-950/20 px-2.5 py-1" data-testid={testId}>
      {SPELL_STATS.map((k) => (
        <div key={k} className="text-center leading-tight">
          <div className="text-[9px] tracking-wide text-muted-foreground">{k}</div>
          <div className="text-sm font-semibold font-mono">{stats[k] || "-"}</div>
        </div>
      ))}
    </div>
  );
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Render rule text with clickable keyword chips inline. */
export function RuleTextWithKeywords({ text, terms }) {
  if (!terms?.length) {
    return <span>{text}</span>;
  }
  // Sort terms by length DESC so longer matches win (e.g. 'Continuous Effect' before 'Effect').
  const sorted = [...terms].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`\\b(${sorted.map(escapeRegex).join("|")})\\b`, "gi");
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) {
      out.push(<span key={`t${i++}`}>{text.slice(last, m.index)}</span>);
    }
    const matched = m[0];
    out.push(
      <KeywordChip
        key={`k${i++}`}
        term={matched}
        inline
        testId="inline-keyword-chip"
      />
    );
    last = m.index + matched.length;
  }
  if (last < text.length) {
    out.push(<span key={`t${i++}`}>{text.slice(last)}</span>);
  }
  return <>{out}</>;
}

export function RuleDetail({ ruleId }) {
  const { navigate } = useNav();
  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getRule(ruleId).then((r) => {
      setRule(r);
      setLoading(false);
    });
  }, [ruleId]);

  if (loading)
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading rule…
      </div>
    );
  if (!rule) return <p>Not found.</p>;

  return (
    <div className="grid lg:grid-cols-[3fr,2fr] gap-6" data-testid="rule-detail">
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <h2 className="text-xl font-semibold">{rule.name}</h2>
            <Badge variant="outline" className={sectionBadgeColor(rule.section)}>
              {sectionLabel(rule.section)}
            </Badge>
            {(rule.duplicate_count || 1) > 1 && (
              <Badge
                variant="outline"
                className="text-rose-300 border-rose-700/60 bg-rose-950/30"
              >
                <Copy className="w-3 h-3 mr-1" />
                {rule.duplicate_count}× duplicate in source data
              </Badge>
            )}
          </div>

          {rule.stats && <div className="mb-3"><SpellStats stats={rule.stats} testId="rule-spell-stats" /></div>}
          <p className="text-base leading-relaxed text-foreground/90 mb-4">
            <RuleTextWithKeywords
              text={rule.text}
              terms={rule.related_rulebook_terms}
            />
          </p>

          {rule.related_rulebook_terms?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-cyan-400" />
                Rulebook keywords (click to look up)
              </h4>
              <div className="flex flex-wrap gap-1.5" data-testid="rulebook-keyword-chips">
                {rule.related_rulebook_terms.map((t) => (
                  <KeywordChip key={t} term={t} />
                ))}
              </div>
            </div>
          )}

          {(rule.duplicate_count || 1) > 1 && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-rose-300 mb-2">
                <AlertTriangle className="w-4 h-4" />
                <h4 className="text-sm font-medium">
                  Data quality — duplicate ability records
                </h4>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                This rule appears under {rule.duplicate_count} separate ability IDs in the game data,
                all with identical name & text. These were merged into a single canonical entry above.
              </p>
              <div className="flex flex-wrap gap-1">
                {(rule.source_ids || []).map((sid) => (
                  <code
                    key={sid}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-background border border-border text-muted-foreground"
                  >
                    {sid}
                  </code>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold mb-1 flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Models/cards with this rule
            <Badge variant="outline" className="ml-1">
              {rule.cards.length}
            </Badge>
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            Click a card to see every rule on its profile.
          </p>
          {rule.cards.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No live cards currently reference this rule.
            </p>
          ) : (
            <div className="space-y-1.5 max-h-[70vh] overflow-y-auto pr-1" data-testid="rule-cards-list">
              {rule.cards.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(VIEWS.card(c.id))}
                  className="w-full text-left p-2 rounded border border-border bg-background/30 hover:border-primary/60 hover:bg-card/70 transition"
                  data-testid={`rule-card-link-${c.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">{c.name}</span>
                    {c.type && (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground shrink-0">
                        {c.type}
                      </Badge>
                    )}
                  </div>
                  {c.faction && (
                    <p className="text-[11px] text-muted-foreground truncate">
                      {c.faction}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
